import { Formik } from 'formik';
import map from 'lodash/map';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import React, { useCallback, useMemo, useState } from 'react';
import * as Yup from 'yup';
import Card from '@mui/material/Card';
import Page from 'containers/Page';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { Flag } from '@mui/icons-material';
import CardActions from 'components/Card/Actions';
import LoadBlur from 'components/LoadBlur';
import RowSpacer from 'components/RowSpacer';
import EnhancedTextarea from 'components/EnhancedTextarea';
import tracker from 'lib/tracker';
import StatusDialog from 'ripple/Components/StatusDialog';
import { Link } from 'found';
import { isPast } from 'date-fns';
import { useApolloClient, useQuery } from '@apollo/client';
import axios from 'axios';
// import HTMLFragment from 'components/HTMLFragment';
import useIsMobile from 'lib/useIsMobile';
import { makeStyles } from '@mui/styles';
import { GET_PROFILE_INFO } from '../schema/apolloQueries/commonQueries';
import { GET_CONTACT_EVENT_LIST_INFO } from '../schema/apolloQueries/customerSupportQueries';

const categories = {
  'Account Question':
    'I have a question about my account (ex: profile information, password, etc.)',
  'Activities Question': 'I have a question about activities for an event',
  'Pack/Shipping Question':
    'I have a shipping question about my pack (ex: where is it?, need to update my address, etc.)',
  'Tech Question':
    'I have a technical question (ex: having trouble logging in, need help using the mobile app, etc.)',
  Other: 'Other',
};

const styles = {
  list: {
    padding: 0,
  },
  listItem: {
    paddingTop: 0,
    paddingBottom: 0,
  },
};

const ZohoCustomerSupportPage = () => {
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);
  const [respTitle, setRespTitle] = useState('');
  const [respType, setRespType] = useState('success');
  const [respMessage, setRespMessage] = useState('');
  const [respAction, setRespAction] = useState('');
  const onPrimaryHandler = useCallback(() => setOpen(false), []);
  const onCancelHandler = useCallback(() => setOpen(false), []);
  const handleCancel = useCallback(() => {
    setError(null);
  }, []);

  const mobile = useIsMobile();

  const useStyles = makeStyles({
    paper: {
      // overflowX: mobile ? 'scroll !important' : 'auto',
      width: mobile ? '250px' : '0',
      // scrollbarWidth: 'none',
    },
  });

  const classes = useStyles();

  const { data } = useQuery(GET_CONTACT_EVENT_LIST_INFO);
  const client = useApolloClient();

  const profileData = client.readQuery({
    query: GET_PROFILE_INFO,
  });

  const trackerData = useMemo(
    () => ({
      ...profileData?.getProfile,
    }),
    [profileData?.getProfile],
  );

  const initialValues = useMemo(
    () => ({
      category: '',
      eventName: '',
      description: '',
    }),
    [],
  );

  const validationSchema = useMemo(
    () => Yup.object().shape({
        category: Yup.string().required('Category is required'),
        eventName: Yup.number().when('category', {
          is: value => value === 'Account Question',
          otherwise: (schema) => schema.min(0, 'Event Name is required')
            .required('Event Name is required'),
          then: (schema) => schema.notRequired(),
        }),
        description: Yup.string()
          .trim()
          .required('Description is required'),
      }),
    [],
  );

  const allEvents = () => {
    const list = [];
    data?.getContactEventList?.events?.forEach(ele => {
      list.push(ele.eventData.name);
    });
    return list;
  };

  return (
    <Page title='Contact Customer Support'>
      {!isPast('2022-10-01') && (
        <Typography style={{ color: '#fff', fontSize: 18, padding: 10 }}>
          We&#39;ve gotten word that our Support Portal has been having
          difficulties the last few days, Friday thru Monday (September 16th
          thru 19th). If you submitted a ticket on those days and haven’t
          received a response yet, please submit another ticket to let us know
          how we can help. Our system is now back up and we are working
          diligently to get back to you as soon as possible.
        </Typography>
      )}
      <Card>
        <Formik
          enableReinitialize
          onSubmit={useCallback(
            ({ ...datas }, { setSubmitting, resetForm }) => {
              const { category, description, eventName } = datas;
              const event = allEvents()[eventName];
              const eventDataID = data?.getContactEventList?.events.filter(
                list => list.eventData.name === event,
              );

              const payload = {
                ...(category !== 'Account Question' ? {
                  cf: {
                    cf_event_id: eventDataID[0].eventData.id,
                    cf_event_name: eventDataID[0].eventData.name,
                  },
                } : {}),
                category,
                subject: categories[category],
                description: `${
                  event ? `<p>Event Name: ${event}</p>` : ''
                }<p>${description}</p>`,
                email: profileData?.getProfile?.personInfo?.email,
                contact: {
                  firstName: profileData?.getProfile?.personInfo?.first,
                  lastName: profileData?.getProfile?.personInfo?.last,
                  email: profileData?.getProfile?.personInfo?.email,
                  phone: profileData?.getProfile?.phoneNumber,
                },
                channel: 'Web',
              };
              const handleError = ({
                message = 'Support ticket submission failed.',
              }) => {
                setSubmitting(false);
                setRespMessage(
                  'Please contact us at <span><a href="mailto:customersupport@ripplestreet.com">customersupport@ripplestreet.com</a></span> and our Customer Support team will follow up with you within 1-2 business days via email. We apologize for the inconvenience.',
                );
                setRespType('error');
                setRespTitle(
                  'Oops, we are currently experiencing technical issues.',
                );
                setRespAction(
                  <a
                    href='mailto:customersupport@ripplestreet.com'
                    style={{}}
                    onClick={onPrimaryHandler}
                  >
                    Email Customer Support
                  </a>,
                );
                setOpen(true);
                tracker.trackGoogleAnalyticsException(message, {
                  ...profileData?.getProfile,
                });
              };
              axios
                .post(
                  `${process.env.MS_API_GATEWAY_URL}/invoke/community-service/v1/ticket`,
                  payload,
                )
                .then(resp => {
                  setSubmitting(false);
                  if (resp?.data) {
                    resetForm();
                    setRespMessage(
                      'Thanks for your submission. Our Customer Support team will follow up with you within 1-2 business days via email.',
                    );
                    setRespTitle('Success!');
                    setRespType('success');
                    setRespAction(
                      <Link
                        to='/discover/events'
                        style={{}}
                        onClick={onPrimaryHandler}
                      >
                        Discover Other Events
                      </Link>,
                    );
                    setOpen(true);
                    tracker.trackGoogleAnalyticsEvent(
                      'HelpDesk',
                      'Issue Reported',
                      trackerData,
                    );
                  } else {
                    handleError(resp);
                  }
                })
                .catch(err => {
                  handleError(err);
                });
            },
            [profileData?.getProfile, allEvents, onPrimaryHandler, trackerData],
          )}
          initialValues={initialValues}
          validationSchema={validationSchema}
          isInitialValid={validationSchema.isValidSync(initialValues)}
        >
          {useCallback(
            ({
              dirty,
              errors,
              isSubmitting,
              isValid,
              resetForm,
              setFieldTouched,
              setFieldValue,
              submitForm,
              values,
            }) => {
              const canSave = dirty && isValid && !isSubmitting;
              const handleChange = (field, val) => {
                setFieldTouched(field, true);
                setFieldValue(field, val);
                if (field === 'category' && val === 'Account Question') {
                  handleChange('eventName', '');
                }
              };
              return (
                <LoadBlur loading={isSubmitting}>
                  {error && (
                    <div
                      style={{
                        color: 'red',
                        textAlign: 'center',
                      }}
                    >
                      {error}
                    </div>
                  )}
                  <List sx={{ mt: 4 }} style={styles.list}>
                    <ListItem sx={{ mb: 4 }} style={styles.listItem}>
                      <FormControl fullWidth>
                        <InputLabel id='category-simple-select-label'>
                          What type of issue do you have?
                        </InputLabel>
                        <Select
                          MenuProps={{
                            classes: {
                              paper: classes.paper,
                            },
                          }}
                          placeholder='Select Issue Type'
                          error={errors?.category}
                          disabled={isSubmitting}
                          // displayEmpty={!values.category}
                          labelId='category-simple-select-label'
                          id='category-simple-select'
                          value={values.category}
                          onChange={event => handleChange('category', event.target.value)}
                          label='What type of issue do you have?'
                        >
                          {map(categories, (val, key, idx) => (
                            <MenuItem
                              // sx={{ fontSize: '12px !important', color: 'red' }}
                              key={key || idx}
                              value={key}
                            >
                              <Typography
                                sx={{
                                  whiteSpace: 'pre-wrap',
                                  display: 'flex',
                                  fontSize: 16,
                                }}
                              >
                                {val}
                              </Typography>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </ListItem>
                    <ListItem style={styles.listItem}>
                      <FormControl fullWidth>
                        <InputLabel id='eventName-simple-select-label'>
                          Which event are you experiencing issues with?
                        </InputLabel>
                        <Select
                          MenuProps={{
                            classes: {
                              paper: classes.paper,
                            },
                          }}
                          disabled={
                            isSubmitting
                            || values.category === ''
                            || values.category === 'Account Question'
                          }
                          onChange={event => handleChange('eventName', event.target.value)}
                          label='eventName event are you experiencing issues with?'
                          value={values.eventName}
                          placeholder='Select Event'
                          // showEmpty={!values.eventName}
                        >
                          {map(allEvents(), (val, key, idx) => (
                            <MenuItem
                              // sx={{ fontSize: '12px !important' }}
                              key={key || idx}
                              value={key}
                            >
                              <Typography
                                sx={{
                                  whiteSpace: 'pre-wrap',
                                  display: 'flex',
                                  fontSize: 16,
                                }}
                              >
                                {val}
                              </Typography>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </ListItem>

                    <ListItem style={styles.listItem}>
                      <Box component='div' flex={1}>
                        <ListItemText
                          style={{ padding: '10px 0' }}
                          primary='What can we help you with?'
                        />
                        <EnhancedTextarea
                          disabled={isSubmitting}
                          errorText={errors?.description}
                          name='description'
                          rows={4}
                          fullWidth
                          onChange={e => {
                            const {
                              target: { value },
                            } = e;
                            handleChange('description', value);
                          }}
                          value={values.description}
                        />
                        <ListItemText secondary="Please describe what you're having issues with and be as specific as possible." />
                      </Box>
                    </ListItem>
                  </List>
                  <CardActions>
                    <RowSpacer />
                    <Button
                      disabled={isSubmitting}
                      variant='contained'
                      size='small'
                      secondary
                      onClick={() => {
                        resetForm();
                        handleCancel();
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      disabled={!canSave}
                      variant='contained'
                      color='primary'
                      size='small'
                      primary={canSave}
                      onClick={submitForm}
                      startIcon={<Flag />}
                    >
                      Submit
                    </Button>
                  </CardActions>
                </LoadBlur>
              );
            },
            [error, allEvents, handleCancel],
          )}
        </Formik>
      </Card>
      <StatusDialog
        open={open}
        type={respType}
        showClose={false}
        title={respTitle}
        message={respMessage}
        primaryAction={respAction}
        onCancel={onCancelHandler}
        secondaryAction={respType !== 'success'}
      />
    </Page>
  );
};

export default ZohoCustomerSupportPage;
